import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatRupiah } from "@/lib/api";
import { PkgDefaultImages, PkgImageCell } from "@/components/admin/appearance/PkgImages";

const GAMES = { royal_dream: "Royal Dream", higgs_domino: "Higgs Domino" };
const num = (v) => v.replace(/\D/g, "").slice(0, 9);
const disc = (p) => (Number(p.original) > Number(p.price) ? Math.round(((p.original - p.price) * 100) / p.original) : 0);

const PkgRow = ({ p, i, game, canManage, set, remove, defaults }) => (
  <tr className="border-t border-white/5" data-testid={`pkg-row-${game}-${i}`}>
    <td className="px-3 py-2"><PkgImageCell p={p} game={game} i={i} defaults={defaults} canManage={canManage} set={set} /></td>
    <td className="px-3 py-2"><input value={p.label} onChange={(e) => set({ label: e.target.value })} disabled={!canManage} maxLength={40} placeholder="120M Koin Emas" className="rk-input !py-1.5 text-sm" data-testid={`pkg-label-${game}-${i}`} /></td>
    <td className="px-3 py-2"><input value={p.price} onChange={(e) => set({ price: num(e.target.value) })} disabled={!canManage} inputMode="numeric" className="rk-input !w-32 !py-1.5 text-sm" data-testid={`pkg-price-${game}-${i}`} /></td>
    <td className="px-3 py-2"><input value={p.original} onChange={(e) => set({ original: num(e.target.value) })} disabled={!canManage} inputMode="numeric" placeholder="0" className="rk-input !w-32 !py-1.5 text-sm" data-testid={`pkg-original-${game}-${i}`} /></td>
    <td className="px-3 py-2 text-xs font-bold text-emerald-400">{disc(p) ? `-${disc(p)}%` : "—"}</td>
    {game === "higgs_domino" ? (
      <td className="px-3 py-2">
        <select value={p.category || "Kartu Domino"} onChange={(e) => set({ category: e.target.value })} disabled={!canManage} className="rk-input !py-1.5 text-xs" data-testid={`pkg-category-${i}`}>
          <option>Kartu Domino</option><option>Kartu Bingo</option>
        </select>
      </td>
    ) : null}
    <td className="px-3 py-2 text-center"><input type="checkbox" checked={p.active} onChange={(e) => set({ active: e.target.checked })} disabled={!canManage} className="h-4 w-4 accent-[#e5b80b]" data-testid={`pkg-active-${game}-${i}`} /></td>
    <td className="px-3 py-2">{canManage ? <button onClick={remove} className="text-rose-400 hover:text-rose-300" aria-label="Hapus paket" data-testid={`pkg-remove-${game}-${i}`}><Trash2 size={14} /></button> : null}</td>
  </tr>
);

const toRows = (list) => list.map((p) => ({ label: p.label, price: String(p.price), original: String(p.original || ""), category: p.category, active: p.active !== false, image: p.image || null, image_url: p.image_url || null }));

const GameEditor = ({ game, initial, canManage, defaults }) => {
  const [rows, setRows] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setRows((r) => r.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const save = async () => {
    setBusy(true);
    try {
      const packages = rows.map((p) => ({ label: p.label.trim(), price: Number(p.price), original: Number(p.original || 0), active: p.active, image: p.image || null, ...(game === "higgs_domino" ? { category: p.category || "Kartu Domino" } : {}) }));
      const { data } = await api.put(`/admin/packages/${game}`, { packages });
      setRows(toRows(data.packages_by_product[game]));
      toast.success(`Harga chip ${GAMES[game]} disimpan`);
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan harga"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rk-glass rounded-2xl p-5" data-testid={`pkg-editor-${game}`}>
      <h3 className="text-lg font-bold">{GAMES[game]}</h3>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
            <tr><th className="px-3 py-2">Gambar</th><th className="px-3 py-2">Nama Chip</th><th className="px-3 py-2">Harga (Rp)</th><th className="px-3 py-2">Harga Coret</th><th className="px-3 py-2">Diskon</th>{game === "higgs_domino" ? <th className="px-3 py-2">Kategori</th> : null}<th className="px-3 py-2">Aktif</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((p, i) => <PkgRow key={`${game}-${i}`} p={p} i={i} game={game} canManage={canManage} defaults={defaults} set={(patch) => set(i, patch)} remove={() => setRows((r) => r.filter((_, j) => j !== i))} />)}
          </tbody>
        </table>
      </div>
      {canManage ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button onClick={() => setRows((r) => [...r, { label: "", price: "", original: "", active: true }])} disabled={rows.length >= 30} className="rk-btn-ghost inline-flex items-center gap-1 text-sm" data-testid={`pkg-add-${game}`}><Plus size={14} /> Tambah Paket</button>
          <button onClick={save} disabled={busy || !rows.length} className="rk-btn-primary inline-flex items-center gap-2 text-sm" data-testid={`pkg-save-${game}`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Simpan Harga
          </button>
          <span className="text-[11px] text-zinc-500">Nama chip wajib diawali jumlah (contoh 120M / 1B). Urutan otomatis berdasarkan harga.</span>
        </div>
      ) : null}
    </div>
  );
};

export const PackagesEditor = ({ canManage }) => {
  const [data, setData] = useState(null);
  const [defaults, setDefaults] = useState(null);
  useEffect(() => {
    api.get("/admin/packages").then((r) => setData(r.data.packages_by_product)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/appearance").then((r) => setDefaults(r.data.pkg_images)).catch(() => {});
  }, []);
  if (!data) return <p className="text-sm text-zinc-500">Memuat…</p>;
  return (
    <div className="space-y-5" data-testid="packages-editor">
      <PkgDefaultImages canManage={canManage} onChange={setDefaults} />
      <p className="text-xs text-zinc-500">Paket nonaktif disembunyikan dari halaman top up. Transaksi lama tidak berubah. Contoh tampilan: {formatRupiah(10000)}.</p>
      {Object.keys(GAMES).map((g) => <GameEditor key={g} game={g} initial={toRows(data[g])} canManage={canManage} defaults={defaults} />)}
    </div>
  );
};
